import { Flex, Icon, Text, useBoolean, useOutsideClick } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'
import IconDownChevron from '~/assets/svgs/icon-down-chevron-bold.svg?react'
import IconUpChevron from '~/assets/svgs/icon-up-chevron-bold.svg?react'
import { useUserState } from '~/atoms/UserState'
import { CircleBadge } from '~/components/shared/CircleBadge'
import { CircleBadgeVariant } from '~/components/shared/componentStyles/CircleBadge.style'
import { MenuPositioningWrapper } from '~/components/shared/Dropdown/Menu'
import QueueStatusMenu from '~/components/shared/Dropdown/QueueStatusMenu'
import { QueueAgentStatus, useCallQueue } from '~/hooks/useCallQueue'
import { useGetCustomStatuses } from '~/queries/customStatusQueries'

import {
  QueueControlButtonVariant,
  useQueueControlButtonStyle,
} from './componentStyles/QueueControlButton.style'

type StatusData = {
  label: string
  secondaryLabel?: string
  selectItemText: string
  badgeVariant?: CircleBadgeVariant
  buttonVariant: QueueControlButtonVariant
}

export const QueueControlButton = () => {
  const [selectedValue, setSelectedValue] = useState<QueueAgentStatus | number>(
    QueueAgentStatus.OutOfQueue,
  )
  const [menuOpen, setMenuOpen] = useBoolean(false)

  const { currentAgentStatus, updateStatus, formattedTime } = useCallQueue()

  const { pbxId } = useUserState()
  const { data: customStatuses } = useGetCustomStatuses(pbxId)

  useEffect(() => {
    if (currentAgentStatus !== selectedValue) {
      setSelectedValue(currentAgentStatus)
    }
  }, [currentAgentStatus])

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (menuOpen) {
        setMenuOpen.off()
      }
    },
  })

  const handleSelect = (value: QueueAgentStatus | number) => {
    if (typeof value === 'number') {
      const status = customStatuses?.find((status) => status.id === value)
      if (status) {
        updateStatus(value as unknown as QueueAgentStatus, {
          id: value,
          label: status?.name ?? '',
          availability: status?.availability,
          startTime: new Date(),
        })
      }
    } else {
      updateStatus(value)
    }
    setMenuOpen.off()
  }

  const statusToLabelData = useMemo(() => {
    const baseStatuses: Record<QueueAgentStatus, StatusData> = {
      [QueueAgentStatus.InQueue]: {
        label: 'In Queue',
        secondaryLabel: formattedTime,
        selectItemText: 'Join Queue',
        badgeVariant: CircleBadgeVariant.Green,
        buttonVariant: QueueControlButtonVariant.InQueue,
      },
      // this may be redundant since we have offline now. will need a little refactoring to remove
      [QueueAgentStatus.OutOfQueue]: {
        label: 'Out of Queue',
        secondaryLabel: undefined,
        selectItemText: 'Leave Queue',
        badgeVariant: undefined,
        buttonVariant: QueueControlButtonVariant.OutOfQueue,
      },
      [QueueAgentStatus.DoNotDisturb]: {
        label: 'Do Not Disturb',
        secondaryLabel: undefined,
        selectItemText: 'Do Not Disturb',
        badgeVariant: CircleBadgeVariant.Red,
        buttonVariant: QueueControlButtonVariant.DoNotDisturb,
      },
      [QueueAgentStatus.Offline]: {
        label: 'Out of Queue',
        secondaryLabel: undefined,
        selectItemText: 'Go Offline',
        badgeVariant: undefined,
        buttonVariant: QueueControlButtonVariant.OutOfQueue,
      },
    }

    const customStatusesMap = customStatuses?.reduce(
      (acc, status) => {
        acc[status.id] = {
          label: status.name,
          secondaryLabel:
            formattedTime && selectedValue === status.id
              ? `${status.availability === 'Online' ? 'Online' : 'Offline'} - ${formattedTime}`
              : `${status.availability === 'Online' ? 'Online' : 'Offline'}`,
          selectItemText: status.name,
          badgeVariant:
            status.availability === 'Online'
              ? CircleBadgeVariant.Blue
              : undefined,
          buttonVariant:
            status.availability === 'Online'
              ? QueueControlButtonVariant.CustomOnline
              : QueueControlButtonVariant.CustomAway,
        }
        return acc
      },
      {} as Record<number, StatusData>,
    )

    return {
      ...baseStatuses,
      ...customStatusesMap,
    }
  }, [customStatuses, selectedValue, formattedTime])

  const styles = useQueueControlButtonStyle({
    variant:
      statusToLabelData[selectedValue]?.buttonVariant ??
      QueueControlButtonVariant.OutOfQueue,
  })

  const currentStatusData =
    statusToLabelData[selectedValue] ??
    statusToLabelData[QueueAgentStatus.OutOfQueue]

  return (
    <MenuPositioningWrapper ref={containerRef}>
      <Flex ref={buttonRef} sx={styles.button} onClick={setMenuOpen.toggle}>
        <Flex sx={styles.buttonContentsContainer}>
          {currentStatusData.badgeVariant && (
            <CircleBadge variant={currentStatusData.badgeVariant} />
          )}
          <Flex sx={styles.textContainer}>
            <Text sx={styles.label}>{currentStatusData.label}</Text>
            {currentStatusData.secondaryLabel && (
              <Text sx={styles.secondaryLabel}>
                {statusToLabelData[selectedValue].secondaryLabel}
              </Text>
            )}
          </Flex>
        </Flex>
        <Icon
          as={menuOpen ? IconUpChevron : IconDownChevron}
          sx={styles.chevronIcon}
        />
      </Flex>
      {menuOpen && (
        <QueueStatusMenu
          customStatuses={customStatuses ?? []}
          selectedValue={selectedValue}
          handleSelect={handleSelect}
          anchorRef={buttonRef}
        />
      )}
    </MenuPositioningWrapper>
  )
}
